import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Paper, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

import {
  CampaignDeliveryStatus,
  CampaignEditor,
  type CampaignChannelOption,
  CampaignList,
  useCampaignDeliveries,
  useCampaignMutations,
  useCampaigns,
} from '@/features/campaigns';
import {
  useChannelsOfCompanyList,
  useCompanyById,
} from '@/features/company';
import { useCompanyModules } from '@/features/company/model/useCompanyModules';
import { usePageTitle } from '@/features/pageTitle';
import type {
  AiCampaignCreateRequestDto,
  AiCampaignDto,
  AiCampaignUpdateRequestDto,
} from '@/shared/api/ai-campaigns';
import type { SafeAutomationConfigDto } from '@/shared/api/companies';
import { PageContainer } from '@/shared/ui';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';

type SaveDraftInput = {
  campaignId?: string;
  create: AiCampaignCreateRequestDto;
  update: AiCampaignUpdateRequestDto;
};

function isSafeAutomationConfig(
  config: Record<string, unknown>
): config is SafeAutomationConfigDto {
  return Array.isArray(config.enabled_sources);
}

export const CampaignsPage = () => {
  const { id: companyId } = useParams<{ id: string }>();
  const { company } = useCompanyById(companyId);
  const campaignsQuery = useCampaigns(companyId);
  const modulesQuery = useCompanyModules(companyId);
  const channelsQuery = useChannelsOfCompanyList(companyId);
  const mutations = useCampaignMutations(companyId);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
    null
  );
  const [isCreating, setIsCreating] = useState(false);

  usePageTitle(`AI-кампании ${company?.name ?? ''}`);

  const campaigns = campaignsQuery.data?.items ?? [];
  const selectedCampaign =
    campaigns.find((campaign) => campaign.id === selectedCampaignId) ?? null;
  const deliveriesQuery = useCampaignDeliveries(selectedCampaign?.id);
  const modules = modulesQuery.data?.modules ?? [];
  const campaignsModule = modules.find((module) => module.key === 'campaigns');
  const safeAutomationModule = modules.find(
    (module) => module.key === 'safe_automation'
  );
  const safeAutomationCampaignEnabled = Boolean(
    safeAutomationModule?.status === 'enabled' &&
      isSafeAutomationConfig(safeAutomationModule.config) &&
      safeAutomationModule.config.enabled_sources.includes('campaign')
  );
  const channels = useMemo<CampaignChannelOption[]>(
    () =>
      (channelsQuery.channelsOfCompanyData ?? []).map((channel) => ({
        id: channel.id,
        name: channel.name ?? channel.id,
        protocol: channel.protocol,
        status: channel.status,
      })),
    [channelsQuery.channelsOfCompanyData]
  );
  const moduleChannelIds = campaignsModule?.scope.channel_ids ?? [];
  const moduleEnabled = campaignsModule?.status === 'enabled';
  const isBusy =
    mutations.create.isPending ||
    mutations.update.isPending ||
    mutations.previewRecipients.isPending ||
    mutations.generateDrafts.isPending ||
    mutations.prepare.isPending ||
    mutations.start.isPending ||
    mutations.pause.isPending ||
    mutations.cancel.isPending;

  useEffect(() => {
    if (selectedCampaignId || isCreating || campaigns.length === 0) return;
    setSelectedCampaignId(campaigns[0].id);
  }, [campaigns, isCreating, selectedCampaignId]);

  useEffect(() => {
    if (!selectedCampaignId) return;
    if (campaigns.some((campaign) => campaign.id === selectedCampaignId)) return;
    setSelectedCampaignId(campaigns[0]?.id ?? null);
  }, [campaigns, selectedCampaignId]);

  if (!companyId) {
    return (
      <PageContainer>
        <NotFoundData title="Компания не найдена" />
      </PageContainer>
    );
  }

  if (campaignsQuery.isPending || modulesQuery.isPending) {
    return (
      <PageContainer>
        <Loader containerSize="small" />
      </PageContainer>
    );
  }

  const saveDraft = async ({
    campaignId,
    create,
    update,
  }: SaveDraftInput): Promise<AiCampaignDto> => {
    const saved = campaignId
      ? await mutations.update.mutateAsync({ id: campaignId, data: update })
      : await mutations.create.mutateAsync(create);

    if (!campaignId && update.content) {
      const updated = await mutations.update.mutateAsync({
        id: saved.id,
        data: update,
      });
      setSelectedCampaignId(updated.id);
      setIsCreating(false);
      return updated;
    }

    setSelectedCampaignId(saved.id);
    setIsCreating(false);
    return saved;
  };

  return (
    <PageContainer>
      <Stack spacing={2.5} sx={{ mt: 2 }}>
        <Stack spacing={0.5}>
          <Typography variant="h5" fontWeight={850}>
            AI-кампании
          </Typography>
          <Typography color="text.secondary">
            Черновики текста, preview получателей, расписание и управляемый запуск
            через Safe Automation.
          </Typography>
        </Stack>

        {campaignsQuery.isError ? (
          <Alert severity="error">{campaignsQuery.error.message}</Alert>
        ) : null}
        {modulesQuery.isError ? (
          <Alert severity="error">{modulesQuery.error.message}</Alert>
        ) : null}
        {channelsQuery.isError ? (
          <Alert severity="error">{channelsQuery.error.message}</Alert>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '320px minmax(0, 1fr)' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <CampaignList
              campaigns={campaigns}
              selectedId={isCreating ? null : selectedCampaignId}
              onSelect={(campaign) => {
                setSelectedCampaignId(campaign.id);
                setIsCreating(false);
              }}
              onCreate={() => {
                setSelectedCampaignId(null);
                setIsCreating(true);
              }}
            />
          </Paper>

          <Stack spacing={2}>
            <CampaignEditor
              companyId={companyId}
              campaign={isCreating ? null : selectedCampaign}
              channels={channels}
              moduleChannelIds={moduleChannelIds}
              moduleEnabled={moduleEnabled}
              safeAutomationCampaignEnabled={safeAutomationCampaignEnabled}
              busy={isBusy}
              onSaveDraft={saveDraft}
              onPreviewRecipients={(data) =>
                mutations.previewRecipients.mutateAsync(data)
              }
              onGenerateDrafts={(data) =>
                mutations.generateDrafts.mutateAsync(data)
              }
              onPrepare={(campaignId) =>
                mutations.prepare.mutateAsync(campaignId)
              }
              onStart={(campaignId) => mutations.start.mutateAsync(campaignId)}
              onPause={(campaignId) => mutations.pause.mutateAsync(campaignId)}
              onCancel={(campaignId) => mutations.cancel.mutateAsync(campaignId)}
            />

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2">Статус доставок</Typography>
                <CampaignDeliveryStatus
                  deliveries={deliveriesQuery.data?.items ?? []}
                />
              </Stack>
            </Paper>
          </Stack>
        </Box>
      </Stack>
    </PageContainer>
  );
};
